import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, Check, X, Trash2, RefreshCw, MessageCircle, Clock, CheckCircle2, XCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusBadgeClass = (status) => {
  if (status === 'pending') return 'bg-amber-100 text-amber-700';
  if (status === 'approved') return 'bg-green-100 text-green-700';
  return 'bg-red-100 text-red-700';
};

const statusLabel = (status) => {
  if (status === 'pending') return 'En attente';
  if (status === 'approved') return 'Publié';
  return 'Rejeté';
};

const AdminReviewsPanel = () => {
  const { getToken } = useAuth();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 10;

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: pageSize });
      if (filter !== 'all') params.set('status', filter);
      const res = await axios.get(`${API}/admin/reviews?${params}`, authHeaders());
      setItems(res.data.items || []);
      setCounts(res.data.counts || counts);
      setTotalPages(res.data.totalPages || 1);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Erreur lors du chargement des avis');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, page]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  // Reset to page 1 when filter changes
  useEffect(() => { setPage(1); }, [filter]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/reviews/${id}/status`, { status }, authHeaders());
      toast.success(status === 'approved' ? 'Avis approuvé et publié' : 'Avis rejeté');
      fetchReviews();
    } catch {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer définitivement cet avis ?')) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, authHeaders());
      toast.success('Avis supprimé');
      fetchReviews();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const FilterChip = ({ value, label, count, icon: Icon, color }) => (
    <button
      onClick={() => setFilter(value)}
      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
        filter === value
          ? `${color} shadow-md`
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
      data-testid={`reviews-filter-${value}`}
    >
      <Icon size={16} />
      {label}
      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
        filter === value ? 'bg-white/30' : 'bg-white text-gray-700'
      }`}>
        {count}
      </span>
    </button>
  );

  return (
    <div>
      {/* Stat row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">Total avis</span>
            <MessageCircle className="text-blue-600" size={20} />
          </div>
          <div className="text-3xl font-bold text-gray-900">{counts.total}</div>
        </div>
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">En attente</span>
            <Clock className="text-amber-600" size={20} />
          </div>
          <div className="text-3xl font-bold text-amber-700">{counts.pending}</div>
        </div>
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">Publiés</span>
            <CheckCircle2 className="text-green-600" size={20} />
          </div>
          <div className="text-3xl font-bold text-green-700">{counts.approved}</div>
        </div>
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">Rejetés</span>
            <XCircle className="text-red-600" size={20} />
          </div>
          <div className="text-3xl font-bold text-red-700">{counts.rejected}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <FilterChip value="pending" label="En attente" count={counts.pending} icon={Clock} color="bg-amber-500 text-white" />
        <FilterChip value="approved" label="Publiés" count={counts.approved} icon={CheckCircle2} color="bg-green-500 text-white" />
        <FilterChip value="rejected" label="Rejetés" count={counts.rejected} icon={XCircle} color="bg-red-500 text-white" />
        <FilterChip value="all" label="Tous" count={counts.total} icon={MessageCircle} color="bg-blue-500 text-white" />

        <button
          onClick={fetchReviews}
          className="ml-auto p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
          title="Actualiser"
          data-testid="reviews-refresh-btn"
        >
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-16 text-gray-500">Chargement...</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-200 rounded-2xl py-16 text-center text-gray-500">
          Aucun avis dans cette catégorie
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((r) => (
            <div
              key={r.id}
              className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow"
              data-testid={`review-card-${r.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${r.color || 'from-blue-500 to-blue-600'} text-white font-bold flex items-center justify-center shadow`}>
                    {r.initials}
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">{r.name}</div>
                    <div className="text-xs text-blue-600">{r.trip}</div>
                  </div>
                </div>
                <div className="flex">
                  {[...Array(r.rating)].map((_, i) => (
                    <Star key={i} size={14} className="fill-amber-400 text-amber-400" />
                  ))}
                </div>
              </div>

              <p className="text-gray-700 text-sm leading-relaxed mb-3 italic">"{r.message}"</p>

              <div className="flex items-center justify-between text-xs text-gray-400 pt-3 border-t border-gray-100">
                <span>{new Date(r.createdAt).toLocaleString('fr-FR')}</span>
                <span className={`px-2 py-0.5 rounded-full font-semibold ${statusBadgeClass(r.status)}`}>
                  {statusLabel(r.status)}
                </span>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 mt-4">
                {r.status !== 'approved' && (
                  <button
                    onClick={() => updateStatus(r.id, 'approved')}
                    className="flex-1 flex items-center justify-center gap-1 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-3 py-2 rounded-lg transition-colors"
                    data-testid={`approve-${r.id}`}
                  >
                    <Check size={16} /> Publier
                  </button>
                )}
                {r.status !== 'rejected' && (
                  <button
                    onClick={() => updateStatus(r.id, 'rejected')}
                    className="flex-1 flex items-center justify-center gap-1 bg-amber-100 hover:bg-amber-200 text-amber-800 text-sm font-semibold px-3 py-2 rounded-lg transition-colors"
                    data-testid={`reject-${r.id}`}
                  >
                    <X size={16} /> Rejeter
                  </button>
                )}
                <button
                  onClick={() => remove(r.id)}
                  className="px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
                  data-testid={`delete-${r.id}`}
                  title="Supprimer"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && total > 0 && totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-6 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            Affichage <span className="font-semibold text-gray-900">{(page - 1) * pageSize + 1}</span>
            {' - '}
            <span className="font-semibold text-gray-900">{Math.min(page * pageSize, total)}</span>
            {' sur '}
            <span className="font-semibold text-gray-900">{total}</span> avis
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="px-3 py-1.5 rounded-lg text-sm font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="reviews-pagination-first"
              aria-label="Première page"
            >
              «
            </button>
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="p-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="reviews-pagination-prev"
              aria-label="Page précédente"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-bold">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              className="p-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="reviews-pagination-next"
              aria-label="Page suivante"
            >
              <ChevronRight size={16} />
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              className="px-3 py-1.5 rounded-lg text-sm font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="reviews-pagination-last"
              aria-label="Dernière page"
            >
              »
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReviewsPanel;
